import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DataTable from "@/components/DataTable";

const STATUS = { scheduled: "Scheduled", completed: "Completed", cancelled: "Cancelled", no_show: "No-show" };
const STATUS_COLOR = {
  scheduled: "bg-[color:var(--primary-light)]/50 text-[color:var(--primary-hover)]",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  no_show: "bg-amber-100 text-amber-700",
};

export default function Appointments() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([api.get("/appointments"), api.get("/patients")]);
      setItems(a.data); setPatients(p.data);
    } catch (err) {
      console.error("Failed to load appointments:", err);
      toast.error("Failed to load");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const bulkDelete = async (ids) => {
    try {
      const { data } = await api.post("/appointments/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} appointment(s)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Bulk delete failed"); }
  };

  const columns = useMemo(() => ([
    {
      key: "scheduled_at", label: "When", sortable: true,
      accessor: (a) => a.scheduled_at,
      render: (a) => new Date(a.scheduled_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }),
    },
    { key: "patient_name", label: "Patient", sortable: true, accessor: (a) => a.patient_name, render: (a) => <span className="font-medium text-[color:var(--ink)]">{a.patient_name}</span> },
    { key: "reason", label: "Reason", accessor: (a) => a.reason || "" },
    { key: "mode", label: "Mode", sortable: true, accessor: (a) => a.mode, render: (a) => <span className="capitalize">{(a.mode || "").replace("_", " ")}</span> },
    { key: "duration_min", label: "Duration", sortable: true, defaultHidden: true, render: (a) => `${a.duration_min || 30} min`, align: "right" },
    {
      key: "status", label: "Status", sortable: true, align: "right",
      accessor: (a) => a.status,
      render: (a) => <span className={`text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full ${STATUS_COLOR[a.status] || ""}`}>{STATUS[a.status] || a.status}</span>,
    },
  ]), []);

  return (
    <div data-testid="appointments-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
        <div>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[color:var(--muted)]">Schedule</p>
          <h1 className="font-heading text-3xl md:text-4xl text-[color:var(--ink)] font-medium mt-1">Appointments</h1>
        </div>
        <ApptDialog open={createOpen} setOpen={setCreateOpen} patients={patients} onSaved={load} />
      </div>

      <DataTable
        storageKey="appointments"
        rows={items}
        columns={columns}
        isAdmin={isAdmin}
        onEdit={isAdmin ? setEditing : undefined}
        onDeleteMany={isAdmin ? bulkDelete : undefined}
        emptyMessage="No appointments scheduled."
      />

      {editing && (
        <ApptDialog
          open={!!editing}
          setOpen={(v) => { if (!v) setEditing(null); }}
          patients={patients}
          existing={editing}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ApptDialog({ open, setOpen, patients, onSaved, existing }) {
  const isEdit = !!existing;
  const empty = { patient_id: "", scheduled_at: "", duration_min: 30, mode: "in_person", reason: "", status: "scheduled" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (existing) {
      // datetime-local needs `YYYY-MM-DDTHH:mm`
      const dt = existing.scheduled_at ? new Date(existing.scheduled_at) : null;
      const local = dt
        ? new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
        : "";
      setForm({
        patient_id: existing.patient_id || "",
        scheduled_at: local,
        duration_min: existing.duration_min ?? 30,
        mode: existing.mode || "in_person",
        reason: existing.reason || "",
        status: existing.status || "scheduled",
      });
    } else {
      setForm(empty);
    }
  }, [existing, open]);

  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...form, scheduled_at: new Date(form.scheduled_at).toISOString(), duration_min: Number(form.duration_min) || 30 };
      if (isEdit) {
        await api.put(`/appointments/${existing.id}`, payload);
        toast.success("Appointment updated");
      } else {
        await api.post("/appointments", payload);
        toast.success("Appointment booked");
      }
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isEdit && (
        <DialogTrigger asChild>
          <Button className="rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="new-apt-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New appointment
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-lg" data-testid="apt-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit appointment" : "New appointment"}</DialogTitle>
          <DialogDescription className="text-[color:var(--muted)]">{isEdit ? "Update timing, mode, reason, or status." : "Schedule a new visit."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Patient *</label>
            <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="apt-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
              <SelectContent>
                {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Date & time *</label>
              <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="mt-1.5" data-testid="apt-datetime" />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Duration (min)</label>
              <Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="mt-1.5" data-testid="apt-duration" />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Mode</label>
              <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                <SelectTrigger className="mt-1.5" data-testid="apt-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_person">In person</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="apt-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Reason</label>
            <Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5" data-testid="apt-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="apt-cancel">Cancel</Button>
          <Button onClick={save} disabled={loading || !form.patient_id || !form.scheduled_at} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="apt-save">
            {loading ? "Saving…" : isEdit ? "Save changes" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
